import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { profileSlice } from "redux/features/profile";

import {
  paymentProcessingSlice,
  COMPLETING_PAYMENT,
  CANCELLING_PAYMENT,
} from "redux/features/paymentProcessing";

import {
  Message,
  Button,
  Container,
  Box,
  Content,
} from "react-bulma-components";

import {
  SCRIPT_READY,
  SCRIPT_ERROR,
  useExternalScript,
} from "util/externalScript";
import { helpAddress } from "config";

import displayHTMLReceipt from "util/receipts";

import Loading from "util/loading";
import { setModal } from "redux/features/modal";

// Moneris installs a message handler on the "window" object that is only
// capable of dealing with its own messages, where the "data" is a JSON-
// encoded string. React development environment uses messages too, but
// its messages have "data" being an object not a string, causing Moneris's
// handler to log error messages to the console when its JSON.parse
// fails.

// We replace Moneris's message handler with our own, that calls Moneris's
// handler only if the message data is a JSON-formatted string.

function passMessageToMoneris(message) {
  let forMoneris = false;
  try {
    JSON.parse(message.data);
    forMoneris = true;
  } catch (e) {
    forMoneris = false;
  }

  if (forMoneris && window.monerisCheckout)
    window.monerisCheckout.prototype.receivePostMessage(message);
}

export function ProcessPayment({ personId, items, onFinished, redisplay }) {
  const processingState = useSelector(paymentProcessingSlice.selector);
  const [initialized, setInitialized] = useState(false);
  const dispatch = useDispatch();

  // When component is first mounted, initiate the creation of a
  // payment-pending transaction on the server.

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      if (items === null && redisplay)
        dispatch(
          paymentProcessingSlice.asyncActions.redisplayOldTransaction(
            personId,
            redisplay
          )
        );
      else
        dispatch(
          paymentProcessingSlice.asyncActions.begin(personId, { items })
        );
    }
  }, [initialized, dispatch, personId, items, redisplay]);

  // Check if the payment-pending transaction has been created on the server
  // yet. If not, set transaction to a dummy object with null entries for
  // things like the external Moneris Javascript URL.

  const [transactionCreated, transaction] =
    initialized && processingState.data
      ? [true, processingState.data]
      : [false, { client_js_url: null }];

  // Check if the Moneris script element has been loaded yet (and initiate
  // loading it if necessary).
  const monerisScriptState = useExternalScript(transaction.client_js_url);

  // Get details of the transaction (if created yet).
  const {
    id: transactionId,
    client_operating_mode: mode,
    ticket,
    completed,
    cancelled,
    approved,
    payment_receipt,
    payment_request,
    registration_receipt,
    warnings,
  } = transaction;
  const cancelling = processingState.apiPending === CANCELLING_PAYMENT;
  const completing = processingState.apiPending === COMPLETING_PAYMENT;

  // Check for various types of error.
  const transactionCreationError =
    transactionCreated || processingState.apiPending || !initialized
      ? null
      : processingState.error || {
          message: "An unknown error occurred",
          details: null,
        };

  const [monerisWidgetError, setMonerisWidgetError] = useState(null);

  const completionError = transactionCreated ? processingState.error : null;
  const cancellationError =
    completionError && completionError.failedOperation === CANCELLING_PAYMENT
      ? completionError
      : null;

  // The Moneris payment processing object, and whether it is needed.
  const [checkoutProcessor, setCheckoutProcessor] = useState(null);
  const processorNeeded =
    transactionCreated &&
    monerisScriptState === SCRIPT_READY &&
    !completed &&
    !cancelled &&
    !completing &&
    !cancelling &&
    !completionError;

  // How to create the processing object

  // Our callback handler. Because this is called asynchronously, don't rely
  // on our checkoutProcessor state variable to hold the Moneris checkout
  // processor object; that won't be set until the render after the processor
  // is created. Instead, receive the processor object as a second argument.
  const monerisCallback = useCallback(
    (message, checkout) => {
      const unpacked = JSON.parse(message);
      const { handler, ticket: callbackTicket } = unpacked || {
        handler: "missing",
        ticket: "missing",
      };

      const whatHappened =
        callbackTicket !== ticket ? "invalid ticket" : handler;
      let action = "cancel";

      switch (whatHappened) {
        case "page_loaded":
          action = null;
          break;
        case "payment_complete":
          action = "finish";
          break;
        case "cancel_transaction":
          break;
        default:
          setMonerisWidgetError({
            message:
              "An error occurred in the payment processing page (" +
              handler +
              ")",
          });
          break;
      }

      if (!action) return;
      dispatch(
        paymentProcessingSlice.asyncActions[action](
          personId,
          transactionId,
          ticket
        )
      );
      checkout.closeCheckout(ticket);
      // window.removeEventListener("message", checkout.receivePostMessage);
      setCheckoutProcessor(null);
    },
    [ticket, personId, transactionId, dispatch]
  );

  useEffect(() => {
    if (processorNeeded && !checkoutProcessor) {
      const checkout = new window.monerisCheckout();
      // Replace Moneris handler with our own, which only passes on
      // messages that are in the format Moneris expects, not React DevTools
      // messages which will cause it to spew out JSON parse errors in the log.
      window.removeEventListener("message", checkout.receivePostMessage);
      window.addEventListener("message", passMessageToMoneris);

      setCheckoutProcessor(checkout);
      checkout.setMode(mode);
      checkout.setCheckoutDiv("monerisCheckout");
      [
        "page_loaded",
        "cancel_transaction",
        "error_event",
        "payment_receipt",
        "payment_complete",
      ].forEach((callbackType) =>
        checkout.setCallback(callbackType, (message) =>
          monerisCallback(message, checkout)
        )
      );
      checkout.startCheckout(ticket);
    }
  }, [
    transactionCreated,
    processorNeeded,
    checkoutProcessor,
    mode,
    ticket,
    monerisCallback,
  ]);

  // The Moneris checkout widget installs, but never removes when it's done,
  // three event handlers on the main "window" object:

  //   (1) "load" via "window.onload=..."
  //   (2) "beforeunload" via "window.onbeforeunload="
  //   (3) "message" via "window.addEventListener('message', ...)

  // When this component is mounted, save the old onload/onbeforeunload
  // properties, and when unmounted, restore them and remove the message
  // listener.

  // Actually, the "message" listener we install ourselves as a modified
  // one, so we remove that instead. Also, technically the beforeunload
  // handler never gets installed because the installation is done in the
  // load handler which never gets run because we add the Moneris scripts
  // dymanically after window load, but we reset it anyway just in case.

  useEffect(() => {
    const previousOnLoad = window.onload;
    const previousOnBeforeUnload = window.onbeforeunload;
    return () => {
      window.onload = previousOnLoad;
      window.onbeforeunload = previousOnBeforeUnload;
      window.removeEventListener("message", passMessageToMoneris);
    };
  }, []);

  useEffect(() => {
    if (checkoutProcessor) {
      dispatch(setModal(true));
      return () => setModal(false);
    } else {
      dispatch(setModal(false));
      return () => {};
    }
  }, [checkoutProcessor, dispatch]);

  const retryWording =
    "Please  try again. If the error persists, please " +
    "contact the Institute's staff.";

  const notificationMessage = transactionCreationError
    ? {
        heading: "Failed to Create Payment Transaction",
        body:
          "Unable to initialize payment transaction: " +
          transactionCreationError.message +
          ". " +
          retryWording,
      }
    : monerisScriptState === SCRIPT_ERROR
    ? {
        heading: "Error Loading Payment Page",
        body: "Unable to load payment page. " + retryWording,
      }
    : monerisWidgetError
    ? {
        heading: "Error in Payment Page",
        body:
          monerisWidgetError.message +
          ". Your payment has been cancelled. " +
          retryWording,
      }
    : cancellationError
    ? {
        heading: "Transaction Cancelled",
        body:
          "Your transaction has been cancelled. However, a problem " +
          "occurred while recording the cancellation: " +
          cancellationError.message +
          ". As a result, you may later " +
          "receive a notification e-mail warning you that your payment " +
          "was not completed.",
      }
    : completionError
    ? {
        heading: "Error Completing Payment",
        body:
          "An error occurred during the processing of your payment: " +
          completionError.message +
          ". Our systems will automatically " +
          "check the status of your payment within the next few hours, " +
          "and you will receive an e-mail message informing you of " +
          "whether or not the payment was successfully received.",
      }
    : cancelled
    ? {
        notError: true,
        heading: "Transaction Cancelled",
        body: "Your transaction has been cancelled.",
      }
    : null;
  const inProgress = !notificationMessage && !completed && !checkoutProcessor;

  const inProgressMessage = (
    <Container className="spaced">
      <Box>
        <Loading />
        {cancelling
          ? "Cancelling Transaction ..."
          : completing
          ? "Completing Transaction ... "
          : "Loading Payment Page .."}
      </Box>
    </Container>
  );

  const {
    response_code,
    iso_response_code,
    reference_no,
    transaction_no,
    transaction_date_time,
    cvd_result_code,
  } = payment_receipt?.cc || {};

  const { first6last4 } = payment_request?.cc || payment_receipt?.cc || {};

  const cardNumber = first6last4
    ? first6last4.replace(/^(....)(..)/, "$1 $2xx xxxx ")
    : "";

  // Moneris Checkout failed to return valid data in its receipt. Card
  // may or may not have actually been charged.
  const monerisCheckoutFailure = response_code === null;

  // Something upstream failed (typically happened with debit cards before
  // we added Visa Debit / Debit MasterCard to our account), and Moneris
  // Checkout reports the upstream null as the string "null". In this case,
  // credit card most likely was NOT actually charged.

  const monerisUpstreamFailure =
    response_code === "null" || transaction_date_time === "null";

  const [declineReason, declineInstructions] = approved
    ? [null, null]
    : monerisCheckoutFailure || monerisUpstreamFailure
    ? [null, null]
    : cvd_result_code === "1N"
    ? [
        "incorrect 3-digit CVV code",
        "Please try again with the correct 3-digit CVV code, or contact your bank if you think the code is correct.",
      ]
    : iso_response_code === "06"
    ? [
        "incorrect expiry date",
        "Please try again with the correct expiry date, or contact your bank if you think the expiry date is correct.",
      ]
    : iso_response_code === "51"
    ? ["insufficient funds", "Please contact your bank."]
    : iso_response_code === "59" || iso_response_code === "83"
    ? [
        "transaction flagged as suspicious by your bank",
        "Please contact your bank to tell them you authorize this charge, then try again.",
      ]
    : iso_response_code === "61"
    ? ["amount is too large", "Please contact your bank."]
    : iso_response_code === "82"
    ? // In theory means invalid CVV code, but we' already checked the
      // cvd_response_code above, so if we are here it means we have the
      // "in theory" invalid CVV code but CVV validation succeeded. Why
      // does this happen? I have no idea. Issue a suitably generic
      // message.
      [
        "error processing 3-digit CVV code",
        "Either you typed in the wrong 3-digit CVV code, or else something went wrong processing it. Please try again, and contact your bank if the error happens again. ",
      ]
    : response_code && response_code.startsWith("4")
    ? [
        null,
        "It was declined by your bank; please contact them to find out why.",
      ]
    : [null, null];

  return (
    <div className="payment-screen">
      <div className="payment-screen-message">
        {notificationMessage ? (
          <Container className="spaced">
            <Message color={notificationMessage.notError ? "black" : "danger"}>
              <Message.Header>{notificationMessage.heading}</Message.Header>
              <Message.Body>
                {notificationMessage.body}

                <div className="buttons spaced">
                  <Button onClick={onFinished}>OK</Button>
                </div>
              </Message.Body>
            </Message>
          </Container>
        ) : inProgress ? (
          inProgressMessage
        ) : completed ? (
          <Container className="spaced">
            <Message {...(approved ? {} : { color: "danger" })}>
              <Message.Header>
                {approved
                  ? "Transaction Approved"
                  : monerisCheckoutFailure || monerisUpstreamFailure
                  ? "Transaction Failed"
                  : "Transaction Declined"}
              </Message.Header>
              <Message.Body>
                {warnings
                  ? warnings.map((warning, i) => (
                      <Message key={i} color="warning">
                        <Message.Body>{warning}</Message.Body>
                      </Message>
                    ))
                  : null}
                {approved ? (
                  <div>
                    <p>The transaction was approved.</p>
                    {displayHTMLReceipt(registration_receipt)}
                  </div>
                ) : monerisUpstreamFailure ? (
                  <Content>
                    <p>
                      An unexpected failure occurred processing your credit
                      card. If you were using a plain debit card: these are not
                      supported; please switch to a regular credit card or a
                      VISA Debit or Debit MasterCard. If you were already using
                      one of those: please try your payment again after a few
                      hours.
                    </p>

                    <p>
                      If this message persists, please contact the Fields
                      Institute's IT support at {helpAddress}.
                    </p>
                  </Content>
                ) : monerisCheckoutFailure ? (
                  <Content>
                    <p>
                      An unexpected failure occurred processing your credit
                      card. This appears to be a system failure at the company
                      that does the credit card processing.{" "}
                      <b>
                        We cannot immediately tell whether or not your card was
                        charged.
                      </b>{" "}
                      You will have received a message saying "transaction
                      declined", and you will appear not to have successfully
                      registered. However, to avoid a possible duplicate
                      transaction, please do not try again just yet.
                    </p>
                    <p>
                      Please contact the Fields Institute's IT support at{" "}
                      {helpAddress}. We will manually investigate. If we
                      discover that your card was charged, we will activate your
                      registration. If your card was not charged, we will ask
                      you to try again.
                    </p>
                  </Content>
                ) : (
                  <div>
                    <p>
                      This card was declined
                      {declineReason ? " (" + declineReason + ")" : null}.
                      {declineInstructions ? " " + declineInstructions : null}
                    </p>
                    <p>
                      <b>Details:</b>
                    </p>
                    <pre>
                      {" "}
                      {`
Card Number:           ${cardNumber}
Transaction Number:    ${transaction_no}
Transaction Date/Time: ${transaction_date_time}
Response Code:         ${response_code}
ISO Response Code:     ${iso_response_code}
Reference Number:      ${reference_no}
`}
                    </pre>
                  </div>
                )}
                <div className="buttons">
                  <Button onClick={onFinished}>OK</Button>
                </div>
              </Message.Body>
            </Message>{" "}
          </Container>
        ) : null}
      </div>
      <div className="payment-screen-body">
        <div id="monerisCheckout"></div>
      </div>
      <div className="payment-screen-bottom"></div>
    </div>
  );
}

/* When we make changes to the above display, in response to discovering new
types of credit card processing situations like the one where Moneris checkout
claimed "declined" but the card was actually processed, we need a way to test
our new display code on a previous transaction. This can be done with the URL
/redisplay-transaction/TRANSACTION_ID in regular user mode or
/staff/for/xxxx/redisplay-transaction/TRANSACTION_ID in staff mode, which
renders the following component to call ProcessPayment in a way that bypasses
all the actual payment stuff and simply requests the server to re-send the
result it sent when the transaction was previously submitted. */

export function RedisplayTransactionResult() {
  const { transactionId } = useParams();

  const { id: personId } = useSelector(profileSlice.selector);

  const navigate = useNavigate();
  return (
    <ProcessPayment
      onFinished={() => navigate("../")}
      personId={personId}
      items={null}
      redisplay={transactionId}
    />
  );
}
